import {
  IConfigurationBuildabilty,
  INotificationDetails,
  IVehicleCountBySeller,
  IVehicleFiltersWithCounts,
  IVehicleSelectedFilters,
} from '@common/interfaces';
import { IModelPackages, IVehicleModel } from '@components/sales/sales-vehicle-picker-page';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { defaultProductionDate } from '@utils/dateTimeFormatter';
import { HashMap, IEquipmentTypes, MAKE } from 'next-common';
import { IEquipmentIdAndSKU } from 'sales-crystallize-common';
import { ReserveButtonStages } from 'types';

interface IInvalidPCodesValidation {
  isInvalidPcodes: boolean;
  errorMessage: string;
}

export interface IOptionChangeRow {
  code?: string;
  codeType?: string;
  name?: string;
}

interface SalesWizardState {
  isCalled: boolean;
  stepMenuData: any;
  calculationId: string;
  tradeInClicked: boolean;
  isLoading: boolean;
  isReserveLoading: boolean;
  isFiltersCountLoading: boolean;
  isPickerLoading: boolean;
  isVehicleLoading: boolean;
  isVehicleConfigLoading: boolean;
  isVehicleListDataLoaded: boolean;
  isUnAvailablePimData: boolean;
  isChangeVehicle: boolean;
  isPCodePageDisabled: boolean;
  invalidPcodesValidation: IInvalidPCodesValidation;
  missingFieldsWarningMessage: string;
  notifications: INotificationDetails[];
  reserveCancelModalStatus: boolean;
  optimisticReserveFailModalStatus: boolean;
  expectedProductionDate: number;
  isShowDeleteModel: boolean;
  isVehicleValidForProceed: boolean;
  isOldConfig: boolean;
  isVehicleSelected: boolean;
  vehicleLocation: string;
  vehicleStatus: string;
  isVehicleRemoved: boolean;
  exactMatchCount: number;
  nonExactMatchCount: number;
  rimsEquipments: IEquipmentTypes;
  selectableEquipments: IEquipmentTypes[];
  listverkEquipments: IEquipmentTypes;
  taklakkEquipments: IEquipmentTypes;
  selectedVariantPath: string;
  hasUpdateEventOccurs: boolean;
  modelOptions: HashMap;
  isStockLoading: boolean;
  configurationBuildability: IConfigurationBuildabilty;
  reserveButtonStage: ReserveButtonStages;
  isSelectedVehicleUnavailable: boolean;
  salesWizardVehicleData: IVehicleModel[];
  vehicleColorId: string;
  vehicleInteriorId: string;
  offerMake: MAKE;
  vehicleSku: string;
  vehicleReservedStatus: boolean;
  vehicleModelCode: string;
  vehicleModelId: string;
  vehicleVariantId: string;
  colorAvailableInPimFlag: boolean;
  interiorAvailableInPimFlag: boolean;
  basePrice: [number, number];
  mappedEquipment: IEquipmentIdAndSKU[];
  selectedModelName: string;
  selectedVariant: IModelPackages;
  selectedColorName: string;
  selectedInteriorName: string;
  articleCodeForLocalOption: string | undefined;
  showCustomerPopup: boolean;
  softLockOrderId: string;
  vehicleFiltersWithCounts: IVehicleFiltersWithCounts;
  selectedVehicleFilters: IVehicleSelectedFilters;
  vehicleCountBySeller: IVehicleCountBySeller;
  subFamilySeries: string | null;
  addedOptions: IOptionChangeRow[];
  removedOptions: IOptionChangeRow[];
  showOptionPopup: boolean;
  isNotBuildable: boolean;
  isNotHealable: boolean;
  pendingShareCode: string | null;
}

const initialState: SalesWizardState = {
  isCalled: false,
  stepMenuData: null,
  calculationId: null,
  tradeInClicked: false,
  isLoading: false,
  isReserveLoading: false,
  isPickerLoading: false,
  isVehicleLoading: false,
  isFiltersCountLoading: false,
  isVehicleConfigLoading: false,
  isVehicleListDataLoaded: false,
  isUnAvailablePimData: false,
  isChangeVehicle: false,
  isPCodePageDisabled: false,
  invalidPcodesValidation: {
    isInvalidPcodes: false,
    errorMessage: '',
  },
  missingFieldsWarningMessage: null,
  notifications: [],
  reserveCancelModalStatus: false,
  optimisticReserveFailModalStatus: false,
  expectedProductionDate: new Date(defaultProductionDate())?.getTime(),
  isShowDeleteModel: false,
  isVehicleValidForProceed: true,
  isOldConfig: false,
  isVehicleSelected: false,
  vehicleLocation: null,
  vehicleStatus: null,
  isVehicleRemoved: false,
  rimsEquipments: null,
  selectableEquipments: [],
  listverkEquipments: null,
  taklakkEquipments: null,
  exactMatchCount: 0,
  nonExactMatchCount: 0,
  selectedVariantPath: null,
  hasUpdateEventOccurs: false,
  modelOptions: null,
  isStockLoading: false,
  configurationBuildability: null,
  reserveButtonStage: ReserveButtonStages.NO_RESERVE_REQUEST,
  isSelectedVehicleUnavailable: false,
  salesWizardVehicleData: [],
  vehicleColorId: null,
  vehicleInteriorId: null,
  offerMake: null,
  vehicleSku: '',
  vehicleReservedStatus: false,
  vehicleModelCode: null,
  vehicleModelId: null,
  vehicleVariantId: null,
  colorAvailableInPimFlag: false,
  interiorAvailableInPimFlag: false,
  basePrice: [0, 0],
  mappedEquipment: [],
  selectedModelName: null,
  selectedVariant: null,
  selectedColorName: null,
  selectedInteriorName: null,
  articleCodeForLocalOption: undefined,
  showCustomerPopup: false,
  softLockOrderId: null,
  subFamilySeries: null,
  addedOptions: [],
  removedOptions: [],
  showOptionPopup: false,
  isNotBuildable: false,
  isNotHealable: false,
  pendingShareCode: null,
  vehicleFiltersWithCounts: {
    modelId: [],
    pimSku: [],
    colorCode: [],
    interiorCode: [],
    exactMatchCode: [],
    locationId: [],
  },
  selectedVehicleFilters: {
    brand: '',
    modelSeries: '',
    modelId: '',
    pimSku: '',
    colorCode: '',
    interiorCode: '',
    options: '',
  },
  vehicleCountBySeller: {
    own: {
      own: 0,
      locationIds: [],
    },
    port: {
      importer: 0,
      locationIds: [],
    },
    other: {
      dealer: 0,
      locationIds: [],
    },
  },
};

const salesWizardSlice = createSlice({
  name: 'salesWizard',
  initialState,
  reducers: {
    setSoftLockOrderId: (state, action: PayloadAction<string>) => {
      state.softLockOrderId = action.payload;
    },
    setShowCustomerPopup: (state, action: PayloadAction<boolean>) => {
      state.showCustomerPopup = action.payload;
    },
    setArticleCodeForLocalOption: (state, action: PayloadAction<string | undefined>) => {
      state.articleCodeForLocalOption = action.payload;
    },
    setSelectedInteriorName: (state, action: PayloadAction<string>) => {
      state.selectedInteriorName = action.payload;
    },
    setSelectedColorName: (state, action: PayloadAction<string>) => {
      state.selectedColorName = action.payload;
    },
    setSelectedVariant: (state, action: PayloadAction<IModelPackages>) => {
      state.selectedVariant = action.payload;
    },
    setSelectedModelName: (state, action: PayloadAction<string>) => {
      state.selectedModelName = action.payload;
    },
    setMappedEquipment: (state, action: PayloadAction<IEquipmentIdAndSKU[]>) => {
      state.mappedEquipment = action.payload;
    },
    setBasePrice: (state, action: PayloadAction<[number, number]>) => {
      state.basePrice = action.payload;
    },
    setInteriorAvailableInPimFlag: (state, action: PayloadAction<boolean>) => {
      state.interiorAvailableInPimFlag = action.payload;
    },
    setColorAvailableInPimFlag: (state, action: PayloadAction<boolean>) => {
      state.colorAvailableInPimFlag = action.payload;
    },
    setVehicleModelId: (state, action: PayloadAction<string>) => {
      state.vehicleModelId = action.payload;
    },
    setVehicleModelCode: (state, action: PayloadAction<string>) => {
      state.vehicleModelCode = action.payload;
    },
    setVehicleReservedStatus: (state, action: PayloadAction<boolean>) => {
      state.vehicleReservedStatus = action.payload;
    },
    setVehicleSku: (state, action: PayloadAction<string>) => {
      state.vehicleSku = action.payload;
    },
    setIsCalled: (state, action: PayloadAction<boolean>) => {
      state.isCalled = action.payload;
    },
    setStepMenuData: (state, action: PayloadAction<any>) => {
      state.stepMenuData = action.payload;
    },
    setCalculationId: (state, action: PayloadAction<string>) => {
      state.calculationId = action.payload;
    },
    setTradeInClicked: (state, action: PayloadAction<boolean>) => {
      state.tradeInClicked = action.payload;
    },
    setIsSalesWizardLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsReserveLoading: (state, action: PayloadAction<boolean>) => {
      state.isReserveLoading = action.payload;
    },
    setIsPickerLoading: (state, action: PayloadAction<boolean>) => {
      state.isPickerLoading = action.payload;
    },
    setIsVehicleLoading: (state, action: PayloadAction<boolean>) => {
      state.isVehicleLoading = action.payload;
    },
    setIsVehicleConfigLoading: (state, action: PayloadAction<boolean>) => {
      state.isVehicleConfigLoading = action.payload;
    },
    setIsVehicleListDataLoaded: (state, action: PayloadAction<boolean>) => {
      state.isVehicleListDataLoaded = action.payload;
    },
    setIsUnAvailablePimData: (state, action: PayloadAction<boolean>) => {
      state.isUnAvailablePimData = action.payload;
    },
    setIsChangeVehicle: (state, action: PayloadAction<boolean>) => {
      state.isChangeVehicle = action.payload;
    },
    setIsPCodePageDisabled: (state, action: PayloadAction<boolean>) => {
      state.isPCodePageDisabled = action.payload;
    },
    setInvalidPcodesValidation: (state, action: PayloadAction<IInvalidPCodesValidation>) => {
      state.invalidPcodesValidation = action.payload;
    },
    setMissingFieldsWarningMessage: (state, action: PayloadAction<string>) => {
      state.missingFieldsWarningMessage = action.payload;
    },
    setNotifications: (state, action: PayloadAction<INotificationDetails[]>) => {
      state.notifications = action.payload;
    },
    setReserveCancelModalStatus: (state, action: PayloadAction<boolean>) => {
      state.reserveCancelModalStatus = action.payload;
    },
    setOptimisticReserveFailModalStatus: (state, action: PayloadAction<boolean>) => {
      state.optimisticReserveFailModalStatus = action.payload;
    },
    setExpectedProductionDate: (state, action: PayloadAction<number>) => {
      state.expectedProductionDate = action.payload;
    },
    setIsShowDeleteModel: (state, action: PayloadAction<boolean>) => {
      state.isShowDeleteModel = action.payload;
    },
    setIsVehicleValidForProceed: (state, action: PayloadAction<boolean>) => {
      state.isVehicleValidForProceed = action.payload;
    },
    setIsOldConfig: (state, action: PayloadAction<boolean>) => {
      state.isOldConfig = action.payload;
    },
    setIsVehicleSelected: (state, action: PayloadAction<boolean>) => {
      state.isVehicleSelected = action.payload;
    },
    setVehicleLocation: (state, action: PayloadAction<string>) => {
      state.vehicleLocation = action.payload;
    },
    setVehicleStatus: (state, action: PayloadAction<string>) => {
      state.vehicleStatus = action.payload;
    },
    setIsVehicleRemoved: (state, action: PayloadAction<boolean>) => {
      state.isVehicleRemoved = action.payload;
    },
    setRimsEquipments: (state, action: PayloadAction<IEquipmentTypes | null>) => {
      state.rimsEquipments = action.payload;
    },
    setSelectableEquipments: (state, action: PayloadAction<IEquipmentTypes[]>) => {
      state.selectableEquipments = action.payload;
    },
    setListverkEquipments: (state, action: PayloadAction<IEquipmentTypes | null>) => {
      state.listverkEquipments = action.payload;
    },
    setExactMatchCount: (state, action: PayloadAction<number>) => {
      state.exactMatchCount = action.payload;
    },
    setNonExactMatchCount: (state, action: PayloadAction<number>) => {
      state.nonExactMatchCount = action.payload;
    },
    setTaklakkEquipments: (state, action: PayloadAction<IEquipmentTypes | null>) => {
      state.taklakkEquipments = action.payload;
    },
    setSelectedVariantPath: (state, action: PayloadAction<string>) => {
      state.selectedVariantPath = action.payload;
    },
    setHasUpdateEventOccurs: (state, action: PayloadAction<boolean>) => {
      state.hasUpdateEventOccurs = action.payload;
    },
    setModelOptions: (state, action: PayloadAction<HashMap>) => {
      state.modelOptions = action.payload;
    },
    setIsStockLoading: (state, action: PayloadAction<boolean>) => {
      state.isStockLoading = action.payload;
    },
    setConfigurationBuildability: (state, action: PayloadAction<IConfigurationBuildabilty>) => {
      state.configurationBuildability = action.payload;
    },
    setReserveButtonStage: (state, action: PayloadAction<ReserveButtonStages>) => {
      state.reserveButtonStage = action.payload;
    },
    setIsSelectedVehicleUnavailable: (state, action: PayloadAction<boolean>) => {
      state.isSelectedVehicleUnavailable = action.payload;
    },
    setSalesWizardVehicleData: (state, action: PayloadAction<IVehicleModel[]>) => {
      state.salesWizardVehicleData = action.payload;
    },
    setVehicleColorId: (state, action: PayloadAction<string>) => {
      state.vehicleColorId = action.payload;
    },
    setVehicleInteriorId: (state, action: PayloadAction<string>) => {
      state.vehicleInteriorId = action.payload;
    },
    setOfferMake: (state, action: PayloadAction<MAKE>) => {
      state.offerMake = action.payload;
    },
    setVehicleVariantId: (state, action: PayloadAction<string>) => {
      state.vehicleVariantId = action.payload;
    },
    setSubFamilySeries: (state, action: PayloadAction<string | null>) => {
      state.subFamilySeries = action.payload;
    },
    setAddedOptions: (state, action: PayloadAction<IOptionChangeRow[]>) => {
      state.addedOptions = action.payload;
    },
    setRemovedOptions: (state, action: PayloadAction<IOptionChangeRow[]>) => {
      state.removedOptions = action.payload;
    },
    setShowOptionPopup: (state, action: PayloadAction<boolean>) => {
      state.showOptionPopup = action.payload;
    },
    setIsNotBuildable: (state, action: PayloadAction<boolean>) => {
      state.isNotBuildable = action.payload;
    },
    setIsNotHealable: (state, action: PayloadAction<boolean>) => {
      state.isNotHealable = action.payload;
    },
    setPendingShareCode: (state, action: PayloadAction<string | null>) => {
      state.pendingShareCode = action.payload;
    },
    setVehicleFiltersWithCounts: (state, action: PayloadAction<IVehicleFiltersWithCounts>) => {
      state.vehicleFiltersWithCounts = {
        ...state.vehicleFiltersWithCounts,
        ...action.payload,
      };
    },
    setSelectedVehicleFilters: (state, action: PayloadAction<IVehicleSelectedFilters>) => {
      state.selectedVehicleFilters = {
        ...state.selectedVehicleFilters,
        ...action.payload,
      };
    },
    resetSelectedVehicleFilters: (state) => {
      state.selectedVehicleFilters = initialState.selectedVehicleFilters;
    },
    setVehicleCountBySeller: (state, action: PayloadAction<IVehicleCountBySeller>) => {
      state.vehicleCountBySeller = action.payload;
    },
    resetVehicleFiltersWithCounts: (state) => {
      state.vehicleFiltersWithCounts = initialState.vehicleFiltersWithCounts;
    },
    setIsFiltersCountLoading: (state, action: PayloadAction<boolean>) => {
      state.isFiltersCountLoading = action.payload;
    },
    onClick: (state, action) => {
      const stepData = action.payload;

      if (stepData?.route) {
        state.stepMenuData = stepData;
        state.calculationId = null;
        state.isCalled = true;
      } else {
        state.stepMenuData = null;
        state.calculationId = null;
        state.isCalled = true;
      }
    },
    onClickSigning: (state, action) => {
      const stepData = action.payload;
      state.stepMenuData = stepData;
      state.isCalled = !state.isCalled;
    },
    onCustomClick: (state) => {
      state.stepMenuData = null;
      state.calculationId = null;
    },
    onCommonNotificationRaised: (state, action: PayloadAction<INotificationDetails>) => {
      const notification = action.payload;
      if (notification.key) {
        const findKeyInNotificationRef = state.notifications?.find((notify) => notify?.key === notification?.key);
        if (!findKeyInNotificationRef) {
          state.notifications = [...(state.notifications || []), notification];
        }
      }
    },
    clearNotifications: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (key) {
        const notifications = state.notifications;
        const findKeyInNotification = notifications?.find((notify) => notify?.key === key);
        if (findKeyInNotification) {
          const afterClear = notifications?.filter((notification) => notification?.key !== key);
          state.notifications = afterClear;
        }
      }
    },
  },
});

export const {
  setIsCalled,
  setStepMenuData,
  setCalculationId,
  onClick,
  setTradeInClicked,
  setIsSalesWizardLoading,
  setIsReserveLoading,
  setIsPickerLoading,
  setIsVehicleLoading,
  setIsVehicleConfigLoading,
  setIsVehicleListDataLoaded,
  onClickSigning,
  setIsUnAvailablePimData,
  setIsChangeVehicle,
  setIsPCodePageDisabled,
  setInvalidPcodesValidation,
  onCustomClick,
  setMissingFieldsWarningMessage,
  setNotifications,
  setReserveCancelModalStatus,
  setOptimisticReserveFailModalStatus,
  onCommonNotificationRaised,
  clearNotifications,
  setExpectedProductionDate,
  setIsShowDeleteModel,
  setIsVehicleValidForProceed,
  setIsOldConfig,
  setIsVehicleSelected,
  setVehicleLocation,
  setVehicleStatus,
  setIsVehicleRemoved,
  setRimsEquipments,
  setSelectableEquipments,
  setListverkEquipments,
  setTaklakkEquipments,
  setSelectedVariantPath,
  setHasUpdateEventOccurs,
  setModelOptions,
  setIsStockLoading,
  setConfigurationBuildability,
  setReserveButtonStage,
  setIsSelectedVehicleUnavailable,
  setSalesWizardVehicleData,
  setExactMatchCount,
  setNonExactMatchCount,
  setVehicleColorId,
  setVehicleInteriorId,
  setOfferMake,
  setVehicleSku,
  setVehicleReservedStatus,
  setVehicleModelCode,
  setVehicleModelId,
  setVehicleVariantId,
  setVehicleFiltersWithCounts,
  setSelectedVehicleFilters,
  resetSelectedVehicleFilters,
  setVehicleCountBySeller,
  resetVehicleFiltersWithCounts,
  setIsFiltersCountLoading,
  setColorAvailableInPimFlag,
  setInteriorAvailableInPimFlag,
  setBasePrice,
  setMappedEquipment,
  setSelectedModelName,
  setSelectedVariant,
  setSelectedColorName,
  setSelectedInteriorName,
  setArticleCodeForLocalOption,
  setShowCustomerPopup,
  setSoftLockOrderId,
  setSubFamilySeries,
  setAddedOptions,
  setRemovedOptions,
  setShowOptionPopup,
  setIsNotBuildable,
  setIsNotHealable,
  setPendingShareCode,
} = salesWizardSlice.actions;
export default salesWizardSlice.reducer;
